import { useEffect, useMemo, useState } from 'react';
import { canAccessClient, canAccessMaterial } from '@/auth/access';
import { useAuth } from '@/hooks/useAuth';
import { useClientes, useMateriais } from '@/hooks/useDB';

type Props = {
  kind: 'cliente' | 'produto';
  value: string;
  disabled?: boolean;
  onSelect: (value: string, code: string) => void;
};

export default function RequestCatalogField({ kind, value, disabled, onSelect }: Props) {
  const { user } = useAuth();
  const [query, setQuery] = useState(value);
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const { clientes, loading: loadingClientes } = useClientes(kind === 'cliente' ? debounced : '__catalog_disabled__');
  const { materiais, loading: loadingMateriais } = useMateriais(kind === 'produto' ? debounced : '__catalog_disabled__');

  useEffect(() => setQuery(value), [value]);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(query.trim()), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  const options = useMemo(() => {
    if (debounced.length < 2) return [];
    const needle = debounced.toLocaleLowerCase('pt-BR');
    if (kind === 'cliente') return clientes
      .filter((item) => canAccessClient(user, item))
      .filter((item) => `${item.codigo} ${item.nome}`.toLocaleLowerCase('pt-BR').includes(needle))
      .slice(0, 15)
      .map((item) => ({ code: item.codigo, label: item.nome, detail: `${item.codigo} · ${item.cidade}/${item.uf}` }));
    return materiais
      .filter((item) => canAccessMaterial(user, item))
      .filter((item) => `${item.codigo} ${item.descricao}`.toLocaleLowerCase('pt-BR').includes(needle))
      .slice(0, 15)
      .map((item) => ({ code: item.codigo, label: item.descricao, detail: item.codigo }));
  }, [clientes, materiais, debounced, kind, user]);

  const loading = kind === 'cliente' ? loadingClientes : loadingMateriais;
  const placeholder = kind === 'cliente' ? 'Digite o código ou nome do cliente' : 'Digite o código ou nome do produto';
  return <div className="relative mt-1">
    <i className="ri-search-line absolute left-3 top-3 text-slate-400" aria-hidden="true" />
    <input
      className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-9 pr-9 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
      value={query}
      disabled={disabled}
      placeholder={placeholder}
      autoComplete="off"
      onFocus={() => setOpen(true)}
      onChange={(event) => { setQuery(event.target.value); setOpen(true); onSelect('', ''); }}
    />
    {value && <button type="button" aria-label={`Trocar ${kind}`} className="absolute right-2 top-2 rounded p-1 text-slate-500 hover:bg-slate-100" onClick={() => { setQuery(''); onSelect('', ''); setOpen(true); }}>✕</button>}
    {open && debounced.length >= 2 && <div className="absolute z-[70] mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl">
      {options.map((option) => <button type="button" key={option.code} className="block w-full rounded-lg px-3 py-2 text-left hover:bg-blue-50" onClick={() => { setQuery(option.label); onSelect(option.label, option.code); setOpen(false); }}>
        <strong className="block text-sm text-slate-900">{option.label}</strong><span className="text-xs text-slate-500">{option.detail}</span>
      </button>)}
      {!loading && options.length === 0 && <p className="p-3 text-center text-sm text-slate-500">Nenhum resultado disponível para seu acesso.</p>}
      {loading && <p className="p-3 text-center text-sm text-slate-500">Buscando...</p>}
    </div>}
    {value && <p className="mt-1 text-xs text-emerald-700"><i className="ri-checkbox-circle-line" /> Selecionado no cadastro</p>}
  </div>;
}
